// eslint-disable-next-line no-restricted-imports -- This IS the sanitize facade; it wraps dompurify.
import DOMPurify, {type Config as DOMPurifyConfig} from 'dompurify'

import type {SafeHTMLString} from './safe-html-string'
import type {Purifier} from './types'

/**
 * Sanitize HTML using DOMPurify with the browser's native window.
 *
 * This is the browser entry point — on the server, the "node" condition
 * in package.json exports resolves to sanitize.server.ts instead.
 *
 * @param html - The HTML string to sanitize
 * @param config - Optional DOMPurify Config object.
 */
export function sanitize(dirty: string | Node, cfg: DOMPurifyConfig & {RETURN_DOM: true}): Node
export function sanitize(dirty: string | Node, cfg: DOMPurifyConfig & {RETURN_DOM_FRAGMENT: true}): DocumentFragment
export function sanitize(dirty: string | Node, cfg: DOMPurifyConfig & {RETURN_TRUSTED_HTML: true}): TrustedHTML
export function sanitize(dirty: string | Node, cfg?: DOMPurifyConfig): SafeHTMLString
export function sanitize(
  dirty: string | Node,
  cfg?: DOMPurifyConfig,
): SafeHTMLString | Node | DocumentFragment | TrustedHTML {
  return DOMPurify.sanitize(dirty, cfg) as SafeHTMLString
}

/**
 * Create a DOMPurify instance for advanced usage (e.g., adding hooks).
 * The returned purifier's `.sanitize()` is typed to return `SafeHTMLString`
 * for standard string sanitization calls.
 */
export function createPurifier(cb?: (purifier: Purifier) => void): Purifier {
  const purifier = DOMPurify() as unknown as Purifier
  if (cb) {
    cb(purifier)
  }
  return purifier
}

export function sanitizeWithPurifier(
  dirty: string | Node,
  purifier: Purifier,
  cfg: DOMPurifyConfig & {RETURN_DOM: true},
): Node
export function sanitizeWithPurifier(
  dirty: string | Node,
  purifier: Purifier,
  cfg: DOMPurifyConfig & {RETURN_DOM_FRAGMENT: true},
): DocumentFragment
export function sanitizeWithPurifier(dirty: string | Node, purifier: Purifier, cfg?: DOMPurifyConfig): SafeHTMLString
export function sanitizeWithPurifier(
  dirty: string | Node,
  purifier: Purifier,
  cfg?: DOMPurifyConfig,
): SafeHTMLString | Node | DocumentFragment {
  return purifier.sanitize(dirty, cfg)
}

// eslint-disable-next-line no-barrel-files/no-barrel-files
export type {Config, Purifier, UponSanitizeAttributeHookEvent} from './types'
